import PropTypes from 'prop-types';
import { useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Divider,
  Tooltip
} from '@mui/material';
import Iconify from 'src/components/iconify';

export default function TaskAttachments({ attachments = [], onChange }) {
  const fileInputRef = useRef(null);
  const [ isUploading, setIsUploading ] = useState(false);
  const [ menuAnchor, setMenuAnchor ] = useState(null);
  const [ selectedAttachment, setSelectedAttachment ] = useState(null);
  const [ previewOpen, setPreviewOpen ] = useState(false);
  const [ previewAttachment, setPreviewAttachment ] = useState(null);

  const handleOpenMenu = (event, attachment) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedAttachment(attachment);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedAttachment(null);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[ 0 ];
    if (!file) return;

    setIsUploading(true);
    try {
      const newAttachment = {
        id: Date.now().toString(),
        name: file.name,
        preview: URL.createObjectURL(file),
        url: URL.createObjectURL(file),
        file,
        date: new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
        time: new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })
      };

      const updatedAttachments = [ ...attachments, newAttachment ];
      onChange(updatedAttachments);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAttachment = () => {
    if (selectedAttachment) {
      const updatedAttachments = attachments.filter(
        attachment => attachment.id !== selectedAttachment.id
      );
      onChange(updatedAttachments);
      handleCloseMenu();
    }
  };

  const handleMakeCover = () => {
    if (selectedAttachment) {
      // First remove cover from all attachments
      const updatedAttachments = attachments.map(attachment => ({
        ...attachment,
        isCover: attachment.id === selectedAttachment.id
      }));
      onChange(updatedAttachments);
      handleCloseMenu();
    }
  };

  const handleOpenPreview = (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
  };

  // Helper to determine file type icon
  const getFileIcon = (filename) => {
    if (!filename) return 'eva:file-outline';

    if (/\.(jpeg|jpg|gif|png|svg|webp)$/i.test(filename)) {
      return 'eva:image-outline';
    } else if (/\.(mp4|webm|ogg|mov|avi)$/i.test(filename)) {
      return 'eva:film-outline';
    } else if (/\.(mp3|wav|ogg|flac)$/i.test(filename)) {
      return 'eva:music-outline';
    } else if (/\.(pdf)$/i.test(filename)) {
      return 'eva:file-text-outline';
    } else if (/\.(doc|docx)$/i.test(filename)) {
      return 'eva:file-text-outline';
    } else if (/\.(xls|xlsx)$/i.test(filename)) {
      return 'eva:grid-outline';
    } else if (/\.(zip|rar|tar|gz)$/i.test(filename)) {
      return 'eva:archive-outline';
    }
    return 'eva:file-outline';
  };

  // Helper to render preview content
  const renderPreviewContent = (attachment) => {
    if (!attachment || !attachment.url) return null;

    if (/\.(jpeg|jpg|gif|png|svg|webp)$/i.test(attachment.name)) {
      return (
        <Box sx={ { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' } }>
          <img
            src={ attachment.url }
            alt={ attachment.name }
            style={ { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' } }
          />
        </Box>
      );
    } else if (/\.(mp4|webm|ogg|mov|avi)$/i.test(attachment.name)) {
      return (
        <Box sx={ { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' } }>
          <video controls style={ { maxWidth: '100%', maxHeight: '100%' } }>
            <source src={ attachment.url } type={ `video/${attachment.name.split('.').pop()}` } />
            Your browser does not support the video tag.
          </video>
        </Box>
      );
    } else if (/\.(pdf)$/i.test(attachment.name)) {
      return (
        <Box sx={ { height: '100%', width: '100%' } }>
          <iframe
            src={ attachment.url }
            title={ attachment.name }
            width="100%"
            height="100%"
            style={ { border: 'none' } }
          />
        </Box>
      );
    } else {
      return (
        <Box
          sx={ {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 3
          } }
        >
          <Iconify icon={ getFileIcon(attachment.name) } sx={ { fontSize: 80, mb: 2, color: 'text.secondary' } } />
          <Typography variant="h6">{ attachment.name }</Typography>
          <Typography variant="body2" color="text.secondary" sx={ { mt: 1 } }>
            This file preview is not available. Please download to view.
          </Typography>
          <Button
            variant="contained"
            href={ attachment.url }
            download={ attachment.name }
            startIcon={ <Iconify icon="eva:download-outline" /> }
            sx={ { mt: 2 } }
          >
            Download
          </Button>
        </Box>
      );
    }
  };

  return (
    <Box sx={ { mb: 3 } }>
      <Box sx={ {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1
      } }>
        <Box sx={ { display: 'flex', alignItems: 'center' } }>
          <Iconify icon="eva:attach-2-fill" sx={ { mr: 1, color: 'text.secondary' } } />
          <Typography variant="subtitle2">Attachments</Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={ <Iconify icon="eva:plus-fill" /> }
          onClick={ handleFileUploadClick }
          disabled={ isUploading }
          sx={ {
            bgcolor: 'action.selected',
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          } }
        >
          { isUploading ? 'Uploading...' : 'Add' }
        </Button>

        <input
          type="file"
          ref={ fileInputRef }
          onChange={ handleFileChange }
          style={ { display: 'none' } }
          accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      </Box>

      { attachments.length > 0 ? (
        <Box sx={ { mt: 2 } }>
          <Typography variant="caption" color="text.secondary" sx={ { pl: 1, mb: 1, display: 'block' } }>
            Files
          </Typography>
          <List sx={ { bgcolor: 'background.paper', borderRadius: 1, py: 0 } }>
            { attachments.map((attachment) => {
              const fileExtension = attachment.name.split('.').pop().toUpperCase();
              const isImage = /\.(jpeg|jpg|gif|png|svg|webp)$/i.test(attachment.name);
              const isVideo = /\.(mp4|webm|ogg|mov|avi)$/i.test(attachment.name);

              return (
                <ListItem
                  key={ attachment.id }
                  button
                  onClick={ () => handleOpenPreview(attachment) }
                  sx={ {
                    borderRadius: 1,
                    my: 0.5,
                    py: 0.5,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  } }
                >
                  <ListItemIcon sx={ { minWidth: 48 } }>
                    { isImage ? (
                      <Box
                        component="img"
                        src={ attachment.url }
                        sx={ {
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          objectFit: 'cover',
                          border: '1px solid',
                          borderColor: 'divider'
                        } }
                      />
                    ) : isVideo ? (
                      <Box
                        sx={ {
                          width: 40,
                          height: 40,
                          bgcolor: 'grey.900',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1
                        } }
                      >
                        <Typography
                          variant="caption"
                          sx={ {
                            color: 'common.white',
                            fontWeight: 'bold'
                          } }
                        >
                          MP4
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={ {
                          width: 40,
                          height: 40,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider'
                        } }
                      >
                        <Typography
                          variant="caption"
                          sx={ {
                            color: 'text.primary',
                            fontWeight: 'bold'
                          } }
                        >
                          { fileExtension.substring(0, 3) }
                        </Typography>
                      </Box>
                    ) }
                  </ListItemIcon>

                  <ListItemText
                    primary={ attachment.name }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Added { attachment.date }, { attachment.time }
                        { attachment.isCover && (
                          <Box
                            component="span"
                            sx={ {
                              ml: 1,
                              px: 0.5,
                              py: 0.25,
                              bgcolor: 'action.selected',
                              borderRadius: 0.5,
                              fontSize: '0.65rem',
                            } }
                          >
                            Cover
                          </Box>
                        ) }
                      </Typography>
                    }
                    primaryTypographyProps={ {
                      variant: 'body2',
                      sx: { fontWeight: 500 }
                    } }
                  />

                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={ (e) => handleOpenMenu(e, attachment) }>
                      <Iconify icon="eva:more-horizontal-fill" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            }) }
          </List>
        </Box>
      ) : (
        <Box
          sx={ {
            p: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            mt: 2
          } }
        >
          <Typography color="text.secondary" variant="body2">
            No attachments yet
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={ <Iconify icon="eva:plus-fill" /> }
            onClick={ handleFileUploadClick }
            sx={ { mt: 1 } }
          >
            Add files
          </Button>
        </Box>
      ) }

      {/* Attachment options menu */ }
      <Menu
        anchorEl={ menuAnchor }
        open={ Boolean(menuAnchor) }
        onClose={ handleCloseMenu }
        PaperProps={ {
          sx: { width: 200, maxWidth: '100%' }
        } }
      >
        <MenuItem onClick={ handleMakeCover }>
          <ListItemIcon>
            <Iconify icon="eva:image-outline" width={ 20 } />
          </ListItemIcon>
          <ListItemText primary="Make cover" />
        </MenuItem>

        <Divider sx={ { my: 0.5 } } />

        <MenuItem onClick={ handleDeleteAttachment } sx={ { color: 'error.main' } }>
          <ListItemIcon>
            <Iconify icon="eva:trash-2-outline" width={ 20 } sx={ { color: 'error.main' } } />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      {/* Preview dialog */ }
      <Dialog
        open={ previewOpen }
        onClose={ handleClosePreview }
        maxWidth="md"
        PaperProps={ {
          sx: {
            width: 600,
            height: 'auto',
            maxHeight: '80vh'
          }
        } }
      >
        <DialogTitle>
          <Box sx={ { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } }>
            <Typography variant="subtitle1" sx={ { flexGrow: 1 } }>
              { previewAttachment?.name }
            </Typography>
            <IconButton onClick={ handleClosePreview } edge="end">
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={ { p: 0, height: 500 } }>
          { renderPreviewContent(previewAttachment) }
        </DialogContent>
      </Dialog>
    </Box>
  );
}

TaskAttachments.propTypes = {
  attachments: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};